// studioAuth.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

export interface StudioDetails {
  id: string
  name: string
  code: string
  slug: string
  market: string
  coordinates: string | null
  address: string
  country: string | null
  state: string
  studioEmail: string
  zipCode: string
  reviewLink: string
  mapLocation: string
  mapImage: string
  priority: number
  createdAt: string
  updatedAt: string
}

export interface StudioAuthState {
  // Form input
  code: string

  // Form state
  selectedStudio: StudioDetails | null

  // Async studio data
  studioDetails: StudioDetails | null
  allStudios: StudioDetails[] | null
  allStudiosStatus: APIStatus
  allStudiosError: string | null
  studioStatus: APIStatus
  error: string | null
}

const initialState: StudioAuthState = {
  code: '',

  selectedStudio: null,

  studioDetails: null,
  allStudios: null,
  studioStatus: APIStatus.idle,
  allStudiosStatus: APIStatus.idle,
  allStudiosError: null,
  error: null,
}

export const getStudioAsync = createAsyncThunk<any, { code: string }, { rejectValue: string }>(
  'studioAuth/getStudio',
  async ({ code }: { code: string }, thunkAPI) => {
    try {
      let response = await axios
        .get(API_URL + '/clinic/auth/code/' + code, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(AppConstant.ADMIN_TOKEN)}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        })
        .then((res) => res)
        .catch((err) => err.response)

      if (response && response.data.code === 'success' && response.data.data) {
        localStorage.setItem(AppConstant.STUDIO_DETAILS, JSON.stringify(response.data.data))
        return thunkAPI.fulfillWithValue(response.data.data)
      } else if (response && response.data.code === 'clinic_not_found') {
        return thunkAPI.rejectWithValue('Studio not found')
      } else {
        return thunkAPI.rejectWithValue('Failed to get studio details')
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to get studio details')
    }
  },
)

export const getAllStudioAsync = createAsyncThunk<any, void, { rejectValue: string }>(
  'studioAuth/getAllStudio',
  async (_, thunkAPI) => {
    try {
      let response = await axios
        .get(API_URL + '/clinic', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(AppConstant.DOCTOR_TOKEN)}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        })
        .then((res) => res)
        .catch((err) => err.response)

      if (response && response.data.code === 'success' && response.data.data) {
        return thunkAPI.fulfillWithValue(response.data.data)
      } else if (response && response.data.code === 'clinic_not_found') {
        return thunkAPI.rejectWithValue('Studio not found')
      } else {
        return thunkAPI.rejectWithValue('Failed to get studio details')
      }
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Failed to get studio details')
    }
  },
)

const studioAuthSlice = createSlice({
  name: 'studioAuth',
  initialState,
  reducers: {
    // Form reducer
    setStudioCode(state, action: PayloadAction<string>) {
      state.code = action.payload
    },

    setSelectedStudio(state, action: PayloadAction<StudioDetails | null>) {
      state.selectedStudio = action.payload
    },

    resetStudioForm() {
      return { ...initialState }
    },

    // Clear studio auth
    clearStudioAuth(state) {
      state.studioDetails = null
      state.allStudios = null
      state.selectedStudio = null
      localStorage.removeItem(AppConstant.STUDIO_DETAILS)
    },

    // Restore studio details from localStorage
    restoreStudioDetails(state) {
      const studioDetailsStr = localStorage.getItem(AppConstant.STUDIO_DETAILS)
      if (studioDetailsStr) {
        try {
          state.studioDetails = JSON.parse(studioDetailsStr)
        } catch (err) {
          console.error('Failed to parse studio details from localStorage', err)
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // getStudioAsync
      .addCase(getStudioAsync.pending, (state) => {
        state.studioStatus = APIStatus.pending
        state.error = null
      })
      .addCase(getStudioAsync.fulfilled, (state, action) => {
        state.studioStatus = APIStatus.fulfilled
        state.studioDetails = action.payload
      })
      .addCase(getStudioAsync.rejected, (state, action) => {
        state.studioStatus = APIStatus.rejected
        state.error = action.payload || 'Failed to load studio'
      })

      // getAllStudioAsync
      .addCase(getAllStudioAsync.pending, (state) => {
        state.allStudiosStatus = APIStatus.pending
        state.error = null
      })

      .addCase(getAllStudioAsync.fulfilled, (state, action) => {
        state.allStudiosStatus = APIStatus.fulfilled
        state.allStudios = [...action.payload].sort((a, b) => a.code.localeCompare(b.code))
      })

      .addCase(getAllStudioAsync.rejected, (state, action) => {
        state.allStudiosStatus = APIStatus.rejected
        state.allStudiosError = action.payload || 'Failed to load studio'
      })
  },
})

export const { setStudioCode, setSelectedStudio, resetStudioForm, clearStudioAuth, restoreStudioDetails } =
  studioAuthSlice.actions

export default studioAuthSlice.reducer
